import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { 
  Measurement, 
  CreateMeasurementRequest, 
  UpdateMeasurementRequest, 
  MeasurementFilters,
  getMeasurements,
  getCustomerMeasurements,
  getMeasurementById,
  createMeasurement,
  updateMeasurement,
  deleteMeasurement
} from '@/utils/measurements'
import { useAuth } from '@/contexts/AuthContext'

interface MeasurementsContextType {
  // State
  measurements: Measurement[]
  customerMeasurements: Record<string, Measurement[]>
  currentMeasurement: Measurement | null
  loading: boolean
  error: string | null
  
  // Actions
  fetchMeasurements: (filters?: MeasurementFilters) => Promise<void>
  fetchCustomerMeasurements: (customerId: string, garmentType?: string) => Promise<void>
  fetchMeasurementById: (id: string) => Promise<void>
  createNewMeasurement: (data: CreateMeasurementRequest) => Promise<Measurement>
  updateExistingMeasurement: (id: string, data: UpdateMeasurementRequest) => Promise<Measurement>
  deleteMeasurementById: (id: string) => Promise<void>
  
  // Utilities
  clearError: () => void
  clearCurrentMeasurement: () => void
}

const MeasurementsContext = createContext<MeasurementsContextType | undefined>(undefined)

interface MeasurementsProviderProps {
  children: ReactNode
}

export const MeasurementsProvider: React.FC<MeasurementsProviderProps> = ({ children }) => {
  // Get authentication state
  const { isAuthenticated } = useAuth()
  
  const [measurements, setMeasurements] = useState<Measurement[]>([])
  const [customerMeasurements, setCustomerMeasurements] = useState<Record<string, Measurement[]>>({})
  const [currentMeasurement, setCurrentMeasurement] = useState<Measurement | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchMeasurements = useCallback(async (filters: MeasurementFilters = {}) => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      console.log('MeasurementsContext: Skipping fetchMeasurements - not authenticated')
      return
    }
    
    try {
      setLoading(true)
      setError(null)
      const result = await getMeasurements(filters)
      setMeasurements(result.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch measurements')
      console.error('Error fetching measurements:', err)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const fetchCustomerMeasurements = useCallback(async (customerId: string, garmentType?: string) => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      console.log('MeasurementsContext: Skipping fetchCustomerMeasurements - not authenticated')
      return
    }
    
    try {
      setLoading(true)
      setError(null)
      const result = await getCustomerMeasurements(customerId, garmentType)
      setCustomerMeasurements(result.measurements)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch customer measurements')
      console.error('Error fetching customer measurements:', err)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const fetchMeasurementById = useCallback(async (id: string) => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setLoading(true)
      setError(null)
      const measurement = await getMeasurementById(id)
      setCurrentMeasurement(measurement)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch measurement')
      console.error('Error fetching measurement:', err)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const createNewMeasurement = useCallback(async (data: CreateMeasurementRequest): Promise<Measurement> => {
    // Only create if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setLoading(true)
      setError(null)
      const newMeasurement = await createMeasurement(data)
      
      // Update local state
      setMeasurements(prev => [newMeasurement, ...prev])
      
      // Update customer measurements if we have them
      if (data.customerId) {
        setCustomerMeasurements(prev => ({
          ...prev,
          [data.customerId]: [...(prev[data.customerId] || []), newMeasurement]
        }))
      }
      
      return newMeasurement
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create measurement'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const updateExistingMeasurement = useCallback(async (id: string, data: UpdateMeasurementRequest): Promise<Measurement> => {
    // Only update if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setLoading(true)
      setError(null)
      const updatedMeasurement = await updateMeasurement(id, data)
      
      // Update local state
      setMeasurements(prev => prev.map(m => m.id === id ? updatedMeasurement : m))
      
      // Update customer measurements if we have them
      if (updatedMeasurement.customerId) {
        setCustomerMeasurements(prev => ({
          ...prev,
          [updatedMeasurement.customerId]: (prev[updatedMeasurement.customerId] || []).map(m => m.id === id ? updatedMeasurement : m)
        }))
      }
      
      // Update current measurement if it's the same one
      if (currentMeasurement?.id === id) {
        setCurrentMeasurement(updatedMeasurement)
      }
      
      return updatedMeasurement
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update measurement'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, currentMeasurement])

  const deleteMeasurementById = useCallback(async (id: string) => {
    // Only delete if authenticated
    if (!isAuthenticated) {
      throw new Error('Authentication required')
    }
    
    try {
      setLoading(true)
      setError(null)
      await deleteMeasurement(id)
      
      // Update local state
      setMeasurements(prev => prev.filter(m => m.id !== id))
      
      // Update customer measurements
      setCustomerMeasurements(prev => {
        const updated = { ...prev }
        Object.keys(updated).forEach(customerId => {
          updated[customerId] = updated[customerId].filter(m => m.id !== id)
        })
        return updated
      })
      
      // Clear current measurement if it's the same one
      if (currentMeasurement?.id === id) {
        setCurrentMeasurement(null)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete measurement'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, currentMeasurement])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const clearCurrentMeasurement = useCallback(() => {
    setCurrentMeasurement(null)
  }, [])

  const value: MeasurementsContextType = {
    // State
    measurements,
    customerMeasurements,
    currentMeasurement,
    loading,
    error,
    
    // Actions
    fetchMeasurements,
    fetchCustomerMeasurements,
    fetchMeasurementById,
    createNewMeasurement,
    updateExistingMeasurement,
    deleteMeasurementById,
    
    // Utilities
    clearError,
    clearCurrentMeasurement,
  }

  return (
    <MeasurementsContext.Provider value={value}>
      {children}
    </MeasurementsContext.Provider>
  )
}

export const useMeasurements = () => {
  const context = useContext(MeasurementsContext)
  if (!context) {
    throw new Error('useMeasurements must be used within a MeasurementsProvider')
  }
  return context
}
